import { useEffect, useRef, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import CopyToClipboard from '@bumble/app/components/CopyToClipboard';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { TabGrid, TabGridStructure } from '@bumble/app/components/TabGrid';
import getDate from '@bumble/app/utils/get-date';
import { addLongPingWatch, claimLongPingRunNotification, removeLongPingWatch } from '@bumble/app/utils/long-ping-watch';
import { notifyLongPingResult } from '@bumble/app/utils/long-ping-notify';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface RouterLongPingProps {
  cpeId: string;
  cpeName?: string;
  ipAddress: string;
  intf: 'wan0' | 'wan1' | 'cellular';
}

const DURATION_DEFAULT = 1;
const POLL_INTERVAL_MS = 5000;
// stop-async-long-ping can take a while to actually finalize on the node
// (it may keep reporting "running" for several polls after being asked to
// stop). Freeze the Stop button for this long so it can't be double-clicked
// while a stop is already in flight; it unfreezes early if the test reaches
// a terminal state sooner.
const STOP_FREEZE_MS = 15000;

const INTERFACE_LABELS: Record<string, string> = {
  wan0: 'Primary WAN',
  wan1: 'Secondary WAN',
  cellular: 'Cellular',
};

const formatInterface = (value?: string) => (value ? INTERFACE_LABELS[value] || value : 'N/A');

const isCompletedStatus = (status?: string) => status === 'success' || status === 'completed';
const isTerminalStatus = (status?: string) => isCompletedStatus(status) || status === 'interrupted' || status === 'failed';
const hasDownloadableReport = (status?: string) => isCompletedStatus(status) || status === 'interrupted';

const getDisplayStatus = (status?: string) => {
  if (isCompletedStatus(status)) return 'Complete';
  if (status === 'interrupted') return 'Interrupted';
  if (status === 'failed') return 'Failed';
  if (status === 'running') return 'Running';
  return null;
};

export const RouterLongPing = ({ cpeId, cpeName, ipAddress, intf }: RouterLongPingProps) => {
  const api = useApi();
  const [durationMinutes, setDurationMinutes] = useState<string>(String(DURATION_DEFAULT));
  const [stopFrozen, setStopFrozen] = useState(false);
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearStopFreeze = () => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    setStopFrozen(false);
  };

  // Clear any pending freeze timeout on unmount so it doesn't fire after
  // this dialog has already closed.
  useEffect(() => () => {
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
  }, []);

  const { data, error, isLoading, refetch } = useQuery(
    ['getAsyncLongPingResult', cpeId],
    () => api.getAsyncLongPingResultApiV1GetAsyncLongPingResultGet({ node_id: cpeId }),
    {
      enabled: !!cpeId,
      retry: false,
      cacheTime: 0,
      refetchInterval: (latest) => (latest?.data?.status === 'running' ? POLL_INTERVAL_MS : false),
      onError: (e: any) => {
        if (e?.status !== 404) apiError(e);
      },
    },
  );

  const result = data?.data;
  const status = result?.status;
  const isNotFound = (error as any)?.status === 404;

  // Tracks whether the user has started/is running a test during this dialog
  // session, so we notify on the resulting completion (even if the very
  // first poll after Start already comes back done, skipping "running"
  // entirely) but don't notify just for opening the dialog on an
  // already-finished test from before.
  const hasActivityRef = useRef(false);

  useEffect(() => {
    if (status === 'running') {
      hasActivityRef.current = true;
    } else if (isTerminalStatus(status)) {
      // No need to keep the Stop button frozen once we can see it's actually done.
      clearStopFreeze();
      // The background LongPingNotifier polls independently — stop watching
      // here so it doesn't also fire a second, delayed notification for the
      // same result.
      removeLongPingWatch(cpeId);
      if (hasActivityRef.current) {
        hasActivityRef.current = false;
        const runId = `${cpeId}:${result?.document_id || result?.start_time_utc || ''}`;
        if (claimLongPingRunNotification(runId)) {
          notifyLongPingResult(cpeName || cpeId, status, result?.result);
        }
      }
    }
  }, [status, cpeId, cpeName, result?.result]);

  const startLongPing = useMutation(api.asyncLongPingApiV1StartAsyncLongPingPost, {
    onSuccess() {
      toast.success('Long ping test started.');
      addLongPingWatch(cpeId, cpeName);
      refetch();
    },
    onError(e: any) {
      apiError(e);
      hasActivityRef.current = false;
      if (e?.status === 400) return;
      refetch();
    },
  });

  const stopLongPing = useMutation(api.stopAsyncLongPingApiV1StopAsyncLongPingPost, {
    onSuccess() {
      refetch();
    },
    onError(e: any) {
      apiError(e);
      refetch();
    },
  });

  const handleStart = () => {
    // Set eagerly (rather than waiting to observe a "running" status) so a
    // test that finishes before the next poll still triggers a notification.
    hasActivityRef.current = true;
    // Must be requested inside this click handler (a user gesture) rather
    // than after the async mutation resolves, or some browsers will ignore it.
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    const parsed = parseInt(durationMinutes, 10);
    startLongPing.mutate({
      node_id: cpeId,
      ip_address: ipAddress,
      interface: intf,
      duration_minutes: Number.isNaN(parsed) || parsed <= 0 ? DURATION_DEFAULT : parsed,
    });
  };

  const handleStop = () => {
    setStopFrozen(true);
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    stopTimeoutRef.current = setTimeout(() => setStopFrozen(false), STOP_FREEZE_MS);
    stopLongPing.mutate({ node_id: cpeId });
  };

  const busy = startLongPing.isLoading || stopLongPing.isLoading || stopFrozen;
  const isRunning = status === 'running';
  const displayStatus = getDisplayStatus(status);
  const remainingSeconds = result?.remaining ?? 0;
  const documentName = status === 'failed' ? '' : result?.document_name;
  const showNoReport = isNotFound || status === 'idle';

  if (isLoading) return <PageLoading content="Loading long ping status..." />;

  const statusFields: TabGridStructure[] = [
    { label: 'Status', content: displayStatus || 'N/A' },
    { label: 'Start Time', content: result?.start_time_utc ? getDate(result.start_time_utc) : 'N/A' },
    {
      label: 'End Time',
      content: result?.end_time_utc && result.end_time_utc !== 'N/A' ? getDate(result.end_time_utc) : 'N/A',
    },
    { label: 'Interface', content: formatInterface(result?.interface) },
    { label: 'Remaining Seconds', content: isRunning ? String(remainingSeconds) : '0' },
  ];

  // Rendered as its own full-width row (separate TabGrid with colWidth={12})
  // since document names are long and get cramped in a shared 1/3-width cell.
  const documentNameField: TabGridStructure[] = [
    {
      label: isRunning ? 'Document Name to Save' : 'Document Name',
      content: documentName ? (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" sx={{ wordBreak: 'break-word', hyphens: 'auto' }}>
            {documentName}
          </Typography>
          <CopyToClipboard content={documentName} ariaLabel="Copy document name" />
        </Box>
      ) : (
        'N/A'
      ),
    },
  ];

  return (
    <Box pt={2}>
      {startLongPing.isLoading && <PageLoading content="Starting long ping test..." />}
      {(stopLongPing.isLoading || stopFrozen) && <PageLoading content="Stopping long ping test..." />}
      <Box display="flex" alignItems="center" sx={{ mb: 6 }}>
        {isRunning ? (
          <Button variant="outlined" color="error" onClick={handleStop} disabled={busy}>
            Stop
          </Button>
        ) : (
          <>
            <TextField
              type="number"
              size="small"
              label="Duration (minutes)"
              variant="outlined"
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              inputProps={{ min: 1 }}
              sx={{ width: 200, mr: 3 }}
            />
            <Button variant="outlined" onClick={handleStart} disabled={busy}>
              Start
            </Button>
          </>
        )}
      </Box>

      {displayStatus ? (
        <>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 3 }}>
            {isRunning ? 'Long Ping Status' : 'Last Long Ping Result'}
          </Typography>
          <TabGrid data={statusFields} />
          <Box sx={{ mt: 3 }}>
            <TabGrid data={documentNameField} colWidth={12} />
          </Box>

          <Typography variant="subtitle2" color="primary" sx={{ mt: 6, mb: 2 }}>
            Result
          </Typography>
          {hasDownloadableReport(status) ? (
            <Button
              variant="outlined"
              component="a"
              href={result?.result}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Test Report
            </Button>
          ) : (
            <Typography variant="body2">{result?.result}</Typography>
          )}
        </>
      ) : (
        showNoReport && (
          <Typography variant="body2" color="text.secondary">
            No long ping test has been run for this device yet.
          </Typography>
        )
      )}
    </Box>
  );
};
